import { useQuery, useInfiniteQuery, UseInfiniteQueryOptions, InfiniteData } from '@tanstack/react-query'
import { AxiosError } from 'axios'

import { clientServiceOfferService } from './service-offer.service'

import { SearchServiceOffersResponse } from '@/features/search/api/search.types'
import { SearchPaginationSort } from '@/features/search/api/search.types'
import axios, { type AxiosRequestHeaders } from 'axios'

import { SearchService } from '@/features/search/api/search.service'
import { createSolarApiClient } from '@/shared/libs/api-client'

import type { ApiHeaders } from '@/shared/libs/context-enhancer'

interface UseSearchServiceOffersParams {
  headers: ApiHeaders
  keyword: string
  filters: {
    hospital: {
      district: {
        neighborhoodVicinityCodes: string[]
      }
    }
  }
  sort: SearchPaginationSort
  pageSize?: number
}

/**
 * Hook for getting related service offers (events)
 */
export const useRelatedServiceOffers = ({
  tagType,
  tagIdList,
  hospitalCountry = 'JP',
  country,
  hospitalAssessmentState,
  hospitalIdList,
  language,
}: {
  tagType: string
  tagIdList: number[]
  hospitalCountry?: string
  country?: string
  hospitalAssessmentState?: string
  hospitalIdList?: number[]
  language: string
}) => {
  return useQuery({
    queryKey: [
      'related-service-offers',
      tagType,
      tagIdList,
      hospitalCountry,
      country,
      hospitalAssessmentState,
      hospitalIdList,
      language,
    ],
    queryFn: () =>
      clientServiceOfferService.getRelatedServiceOffers({
        tagType,
        tagIdList,
        hospitalCountry,
        country,
        hospitalAssessmentState,
        hospitalIdList,
        _language: language,
      }),
    enabled: !!tagType && !!tagIdList?.length,
  })
}

/**
 * Hook for searching service offers with infinite scroll
 */
export const useSearchServiceOffers = ({
  params,
  options,
}: {
  params: UseSearchServiceOffersParams
  options?: Partial<
    Omit<
      UseInfiniteQueryOptions<
        SearchServiceOffersResponse,
        AxiosError,
        InfiniteData<SearchServiceOffersResponse>,
        SearchServiceOffersResponse,
        ['searchServiceOffers', string, any, SearchPaginationSort, number],
        number
      >,
      'queryKey' | 'queryFn' | 'getNextPageParam' | 'initialPageParam'
    >
  >
}) => {
  const { headers, keyword, filters, sort, pageSize = 20 } = params

  return useInfiniteQuery<
    SearchServiceOffersResponse,
    AxiosError,
    InfiniteData<SearchServiceOffersResponse>,
    ['searchServiceOffers', string, any, SearchPaginationSort, number],
    number
  >({
    queryKey: ['searchServiceOffers', keyword, filters, sort, pageSize],
    queryFn: ({ pageParam }) => {
      const searchService = new SearchService(
        createSolarApiClient({ headers: headers as unknown as AxiosRequestHeaders }),
      )
      return searchService.searchServiceOffers({
        keyword,
        filters,
        pagination: {
          pageIndex: pageParam,
          pageSize,
          sort,
        },
      })
    },
    getNextPageParam: (lastPage) => {
      const currentPageIndex = lastPage.currentPageIndex
      const totalPages = Math.ceil(lastPage.totalElementNumber / pageSize)
      return currentPageIndex < totalPages - 1 ? currentPageIndex + 1 : undefined
    },
    initialPageParam: 0,
    ...options,
  })
}
